import { EligibleRegisterGroup, RegisterCompositionGroup } from '@schooly/api';

import {
  isSessionScope,
  isSessionTab,
  REGISTER_SESSIONS,
  RegisterGroupIds,
  RegisterGroupTarget,
  RegisterScope,
  RegisterSession,
  RegisterTab,
  RosterGroup,
  toRegisterSession,
} from '../types';

const bySession = <T>(build: (session: RegisterSession) => T): Record<RegisterSession, T> =>
  Object.fromEntries(REGISTER_SESSIONS.map((session) => [session, build(session)])) as Record<
    RegisterSession,
    T
  >;

export const emptyGroupIds = (): RegisterGroupIds => ({
  ...bySession<string[]>(() => []),
  periods: {},
});

export const getGroupIdsForScope = (groupIds: RegisterGroupIds, scope: RegisterScope): string[] =>
  isSessionScope(scope) ? groupIds[scope.session] : (groupIds.periods[scope.periodId] ?? []);

export const getGroupsForTab = (
  groups: RosterGroup[],
  groupIds: RegisterGroupIds,
  tab: RegisterTab,
): RosterGroup[] => {
  if (isSessionTab(tab)) {
    return groups.filter((group) => groupIds[tab].includes(group.group_id));
  }

  return groups.filter((group) =>
    Object.values(groupIds.periods).some((ids) => ids.includes(group.group_id)),
  );
};

type MutateGroupIdParams = {
  groupIds: RegisterGroupIds;
  id: string;
  target: RegisterGroupTarget;
};

const mutateGroupIdAtTarget = (
  { groupIds, id, target }: MutateGroupIdParams,
  mutate: (current: string[], id: string) => string[],
): RegisterGroupIds => {
  const { session, periodId } = target;
  let next = groupIds;

  if (session) {
    next = { ...next, [session]: mutate(next[session], id) };
  }

  if (periodId) {
    next = {
      ...next,
      periods: { ...next.periods, [periodId]: mutate(next.periods[periodId] ?? [], id) },
    };
  }

  return next;
};

export const addGroupIdToTarget = (params: MutateGroupIdParams): RegisterGroupIds =>
  mutateGroupIdAtTarget(params, (current, id) =>
    current.includes(id) ? current : [...current, id],
  );

export const removeGroupIdFromTarget = (params: MutateGroupIdParams): RegisterGroupIds =>
  mutateGroupIdAtTarget(params, (current, id) => current.filter((groupId) => groupId !== id));

export const hasSelectedGroups = (groupIds: RegisterGroupIds) =>
  REGISTER_SESSIONS.some((session) => groupIds[session].length) ||
  Object.values(groupIds.periods).some((ids) => ids.length > 0);

export const getAutoSelectedGroupIds = (
  eligibleGroups: EligibleRegisterGroup[],
): RegisterGroupIds => {
  const result = emptyGroupIds();

  for (const group of eligibleGroups) {
    for (const { session, scheduled } of group.sessions) {
      const registerSession = toRegisterSession(session);
      if (!scheduled || !registerSession) continue;

      result[registerSession].push(group.group_id);
    }

    for (const { period_id, scheduled } of group.slots) {
      if (!scheduled) continue;

      result.periods[period_id] = [...(result.periods[period_id] ?? []), group.group_id];
    }
  }

  return result;
};

export const seedGroupIdsFromComposition = (
  groups: RegisterCompositionGroup[],
): RegisterGroupIds => {
  const result = emptyGroupIds();

  for (const group of groups) {
    for (const session of group.sessions) {
      result[session].push(group.group_id);
    }

    for (const periodId of group.periods) {
      result.periods[periodId] = [...(result.periods[periodId] ?? []), group.group_id];
    }
  }

  return result;
};
